import { SlideName } from "./types";

export type SlideMeta = {
  id: SlideName;
  slideNode: HTMLElement;
  goToCTAState?: () => Promise<void> | void; // prepare the slide to show CTA
  resetToStart?: () => Promise<void> | void; // prepare the slide to be scrolled in from above
  resetToEnd?: () => Promise<void> | void; // prepare the slide to be scrolled in from below
};

type SlideId = string | HTMLElement;

export class SlidesManager {
  private static slidesById: Map<string, SlideMeta> = new Map();
  private static slidesByNode: Map<HTMLElement, SlideMeta> = new Map();
  private static _isScrollingThrough = false;
  private static _scrollingThroughCallbacks = new Set<
    (value: boolean) => void
  >();

  private static updateScrollingThroughCallbacks() {
    this._scrollingThroughCallbacks.forEach((callback) => {
      callback(this._isScrollingThrough);
    });
  }

  static addSlide(slide: SlideMeta) {
    this.slidesById.set(slide.id, slide);
    this.slidesByNode.set(slide.slideNode, slide);
  }

  static getSlide(id: SlideId) {
    return typeof id === "string"
      ? this.slidesById.get(id)
      : this.slidesByNode.get(id);
  }

  static removeSlide(id: SlideId): void {
    const slide = this.getSlide(id);

    if (slide) {
      this.slidesByNode.delete(slide.slideNode);
      this.slidesById.delete(slide.id);
    }
  }

  static getPreviousSlide(id: SlideId) {
    const slide = this.getSlide(id);

    const previousSlide = (slide?.slideNode.previousElementSibling ??
      undefined) as HTMLElement | undefined;

    if (previousSlide != null && previousSlide.dataset.slide != null) {
      return this.getSlide(previousSlide);
    }
  }

  static getNextSlide(id: SlideId) {
    const slide = this.getSlide(id);

    const nextSlide = (slide?.slideNode.nextElementSibling ?? undefined) as
      | HTMLElement
      | undefined;

    if (nextSlide != null && nextSlide.dataset.slide != null) {
      return this.getSlide(nextSlide);
    }
  }

  static set isScrollingThrough(value: boolean) {
    this._isScrollingThrough = value;
    this.updateScrollingThroughCallbacks();
  }

  static getIsScrollingThrough = () => {
    return this._isScrollingThrough;
  };

  static subscribeToScrollingThrough = (
    callback: (isScrolling: boolean) => void,
  ) => {
    this._scrollingThroughCallbacks.add(callback);

    return () => this._scrollingThroughCallbacks.delete(callback);
  };
}
